import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createLoyaltyCampaign,
  createLoyaltyFlow,
  deleteLoyaltyCampaign,
  deleteLoyaltyFlow,
  getLoyaltyBrandLogo,
  getLoyaltyCampaignHistory,
  getLoyaltyCampaigns,
  getLoyaltyFlowCustomerDetail,
  getLoyaltyFlowCustomers,
  getLoyaltyFlowHistory,
  getLoyaltyFlows,
  previewLoyaltyCampaign,
  previewLoyaltyCampaignAudience,
  sendLoyaltyCampaign,
  testSendLoyaltyCampaign,
  updateLoyaltyCampaign,
  updateLoyaltyFlow,
} from '../../../gateways/breadstack/loyaltyMarketingApis'
import type {
  CampaignAudience,
  CreateCampaignInput,
  CreateFlowInput,
  LoyaltyFlowCustomerIdentity,
  PreviewCampaignInput,
  TestSendCampaignInput,
  UpdateCampaignInput,
  UpdateFlowInput,
} from '../../../types/loyaltyMarketing'

// Query keys — the storeId lives in the key so every store's marketing data
// caches independently and invalidation is scoped per store.
export const loyaltyCampaignsQueryKey = (storeId: string) =>
  ['loyalty', 'campaigns', storeId] as const
export const loyaltyFlowsQueryKey = (storeId: string) =>
  ['loyalty', 'flows', storeId] as const
export const loyaltyFlowHistoryQueryKey = (storeId: string, flowId: string) =>
  ['loyalty', 'flow-history', storeId, flowId] as const
export const loyaltyCampaignHistoryQueryKey = (
  storeId: string,
  campaignId: string,
) => ['loyalty', 'campaign-history', storeId, campaignId] as const
export const loyaltyBrandLogoQueryKey = (storeId: string) =>
  ['loyalty', 'brand-logo', storeId] as const

const FLOW_HISTORY_PAGE_SIZE = 10
const FLOW_CUSTOMERS_PAGE_SIZE = 25
const CAMPAIGN_HISTORY_PAGE_SIZE = 20

// ── Campaigns ────────────────────────────────────────────────────────────────

export const useLoyaltyCampaignsQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltyCampaignsQueryKey(storeId),
    queryFn: () => getLoyaltyCampaigns(storeId),
    enabled: Boolean(storeId),
  })

export const useCreateLoyaltyCampaign = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      createLoyaltyCampaign(storeId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltyCampaignsQueryKey(storeId),
      }),
  })
}

// Audience counts for the builder's selected-audience card. A mutation (not a
// query) because the builder debounces the audience snapshot and fires it
// explicitly, like the segment preview.
export const useCampaignAudiencePreview = () =>
  useMutation({
    mutationFn: ({
      storeId,
      audience,
    }: {
      storeId: string
      audience: CampaignAudience
    }) => previewLoyaltyCampaignAudience(storeId, audience),
  })

// Preview is a pure server render — no cache to invalidate. The builder
// debounces its content snapshot (400 ms) and reads subject/html off the
// resolved value.
export const usePreviewLoyaltyCampaign = () =>
  useMutation({
    mutationFn: ({
      storeId,
      input,
    }: {
      storeId: string
      input: PreviewCampaignInput
    }) => previewLoyaltyCampaign(storeId, input),
  })

// "Test Before Sending" — a one-shot send, so a mutation with nothing to
// invalidate: a test send never touches campaign stats, history or the list
// (the server enqueues an inert worker task). Mirrors usePreviewLoyaltyCampaign.
export const useTestSendLoyaltyCampaign = () =>
  useMutation({
    mutationFn: ({
      storeId,
      input,
    }: {
      storeId: string
      input: TestSendCampaignInput
    }) => testSendLoyaltyCampaign(storeId, input),
  })

// → { brandLogoUrl, brandColor } ('' when the store has none).
export const useLoyaltyBrandLogoQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltyBrandLogoQueryKey(storeId),
    queryFn: () => getLoyaltyBrandLogo(storeId),
    enabled: Boolean(storeId),
    staleTime: 5 * 60 * 1000,
  })

export const useSendLoyaltyCampaign = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (campaignId: string) =>
      sendLoyaltyCampaign(storeId, campaignId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltyCampaignsQueryKey(storeId),
      }),
  })
}

// Lifecycle transition PATCH (schedule / pause / resume / revert-to-draft).
// Also invalidates the campaign's history feed so the dialog reflects the
// just-logged status_changed event on next open.
export const useUpdateLoyaltyCampaign = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      campaignId,
      input,
    }: {
      campaignId: string
      input: UpdateCampaignInput
    }) => updateLoyaltyCampaign(storeId, campaignId, input),
    onSuccess: (_data, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: loyaltyCampaignsQueryKey(storeId),
      })
      queryClient.invalidateQueries({
        queryKey: loyaltyCampaignHistoryQueryKey(storeId, campaignId),
      })
    },
  })
}

// Campaign lifecycle history (cursor-paginated audit log) — mirrors the flow
// history hook: infinite query keyed per campaign, next page on demand.
export const useLoyaltyCampaignHistoryQuery = (
  storeId: string,
  campaignId: string,
  options: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: loyaltyCampaignHistoryQueryKey(storeId, campaignId),
    queryFn: ({ pageParam }) =>
      getLoyaltyCampaignHistory(storeId, campaignId, {
        limit: CAMPAIGN_HISTORY_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled:
      Boolean(storeId) && Boolean(campaignId) && (options.enabled ?? true),
  })

export const useDeleteLoyaltyCampaign = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (campaignId: string) =>
      deleteLoyaltyCampaign(storeId, campaignId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltyCampaignsQueryKey(storeId),
      }),
  })
}

// ── Flows ────────────────────────────────────────────────────────────────────

export const useLoyaltyFlowsQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltyFlowsQueryKey(storeId),
    queryFn: () => getLoyaltyFlows(storeId),
    enabled: Boolean(storeId),
  })

export const useCreateLoyaltyFlow = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFlowInput) => createLoyaltyFlow(storeId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltyFlowsQueryKey(storeId),
      }),
  })
}

export const useUpdateLoyaltyFlow = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      flowId,
      input,
    }: {
      flowId: string
      input: UpdateFlowInput
    }) => updateLoyaltyFlow(storeId, flowId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltyFlowsQueryKey(storeId),
      }),
  })
}

export const useDeleteLoyaltyFlow = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (flowId: string) => deleteLoyaltyFlow(storeId, flowId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: loyaltyFlowsQueryKey(storeId),
      }),
  })
}

// Activity › Customer view: the flow's customers, searched server-side and paged.
export const useLoyaltyFlowCustomersQuery = (
  storeId: string,
  flowId: string,
  search: string,
  options: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: ['loyalty-flow-customers', storeId, flowId, search] as const,
    queryFn: ({ pageParam }) =>
      getLoyaltyFlowCustomers(storeId, flowId, {
        search,
        offset: pageParam,
        limit: FLOW_CUSTOMERS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    enabled: Boolean(storeId) && Boolean(flowId) && (options.enabled ?? true),
  })

export const useLoyaltyFlowCustomerDetailQuery = (
  storeId: string,
  flowId: string,
  customer: LoyaltyFlowCustomerIdentity,
) =>
  useQuery({
    queryKey: [
      'loyalty-flow-customer-detail',
      storeId,
      flowId,
      customer.customer_id,
      customer.email,
    ] as const,
    queryFn: () => getLoyaltyFlowCustomerDetail(storeId, flowId, customer),
    enabled: Boolean(storeId) && Boolean(flowId),
    retry: false,
  })

// Flow history (cursor-paginated audit log). Mirrors the customer-activity feed:
// page over already-fetched react-query pages and fetch the next on demand.
export const useLoyaltyFlowHistoryQuery = (
  storeId: string,
  flowId: string,
  options: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: loyaltyFlowHistoryQueryKey(storeId, flowId),
    queryFn: ({ pageParam }) =>
      getLoyaltyFlowHistory(storeId, flowId, {
        limit: FLOW_HISTORY_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: Boolean(storeId) && Boolean(flowId) && (options.enabled ?? true),
  })
